// ApiErrorDispatcher — centralized ApiError → UI action wiring per
// DESIGN.md §10, task 44.
//
// Why a React component (not a plain function): the handler needs to
// reach into three providers — toasts (`useToast`), role
// (`useRole.forcePickerOpen`), and (for completeness) the navigator for
// 401 fallbacks. Packaging it as a context provider means pages just do:
//
//   const handleApiError = useApiErrorHandler();
//   try { ... }
//   catch (err) { if (err instanceof ApiError) handleApiError(err); else throw err; }
//
// ...and every failure surface gets DESIGN.md §10 treatment, including
// the `X-Request-ID` tail on every toast, without the page having to
// remember.
//
// Page-owned inline handling (e.g. /admin/grants row-greying for
// `role_not_held`) is supported via the `inlineHandler` option — the
// page returns `true` for the classes it wants to absorb, and the
// dispatcher suppresses its default toast.

import { useCallback, useMemo, useRef, type ReactNode } from "react";
import { useToast } from "@/components/toast-types";
import { useRole } from "@/role/context";
import { ApiError, clearCurrentRole } from "./client";
import {
  ApiErrorDispatcherContext,
  type DispatchApiError,
  type DispatchOptions,
} from "./errorDispatcherContext";
import { classifyApiError, type ErrorClass } from "./errorHandler";

function titleWithAction(base: string, actionLabel: string | undefined): string {
  if (actionLabel === undefined || actionLabel === "") return base;
  return `${actionLabel} — ${base.charAt(0).toLowerCase()}${base.slice(1)}`;
}

export function ApiErrorDispatcher({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const toast = useToast();
  const { currentRole, forcePickerOpen } = useRole();

  // Use refs for values that change frequently but shouldn't cause the
  // dispatch callback to be recreated. This prevents downstream effects
  // (which depend on handleApiError) from re-firing on every role change.
  const currentRoleRef = useRef(currentRole);
  currentRoleRef.current = currentRole;
  const forcePickerOpenRef = useRef(forcePickerOpen);
  forcePickerOpenRef.current = forcePickerOpen;

  const dispatch = useCallback<DispatchApiError>(
    (err: ApiError, options?: DispatchOptions) => {
      const cls: ErrorClass = classifyApiError(err);

      if (options?.inlineHandler?.(cls) === true) return;

      switch (cls.kind) {
        case "session_expired": {
          return;
        }

        case "role_required": {
          // eslint-disable-next-line no-console
          console.warn(
            `[api-dispatch] role_required — forcing role picker. reqID=${cls.requestId ?? "-"}`,
          );
          const opened = forcePickerOpenRef.current();
          if (opened) {
            toast.danger({
              title: titleWithAction("Role required", options?.actionLabel),
              description: "Pick a role to continue.",
              requestId: cls.requestId,
            });
          } else {
            toast.danger({
              title: "No roles available for your account",
              description: "Contact your admin to be added to a role-bound group.",
              requestId: cls.requestId,
            });
          }
          return;
        }

        case "invalid_role": {
          const attempted = cls.attemptedRole ?? currentRoleRef.current ?? "unknown";
          // eslint-disable-next-line no-console
          console.warn(
            `[api-dispatch] invalid_role — attempted='${attempted}', clearing and re-prompting. reqID=${cls.requestId ?? "-"}`,
          );
          clearCurrentRole();
          const opened = forcePickerOpenRef.current();
          toast.danger({
            title: titleWithAction("Invalid role", options?.actionLabel),
            description: opened
              ? `Invalid role '${attempted}'. Resetting to role picker.`
              : `Invalid role '${attempted}'. No alternate role available.`,
            requestId: cls.requestId,
          });
          return;
        }

        case "role_not_held": {
          const role = cls.role ?? currentRoleRef.current ?? "your current role";
          const loc = cls.locationId ?? "this site";
          toast.warning({
            title: titleWithAction(
              "Role not held at this site",
              options?.actionLabel,
            ),
            description: `Your current role (${role}) has no grant at ${loc}.`,
            requestId: cls.requestId,
          });
          return;
        }

        case "role_insufficient": {
          // eslint-disable-next-line no-console
          console.error(
            `[ui-bug] role_insufficient_for_action — a disabled button was clickable. reqID=${cls.requestId ?? "-"}`,
            err.body,
          );
          toast.warning({
            title: titleWithAction(
              "Action not permitted for your current role",
              options?.actionLabel,
            ),
            description:
              "This is a UI bug — the button should have been disabled. Please report the request ID.",
            requestId: cls.requestId,
          });
          return;
        }

        case "provisioning_pending": {
          toast.info({
            title: "Access pending provisioning",
            description: "Retry in 1–2 minutes.",
            requestId: cls.requestId,
          });
          return;
        }

        case "partial_success": {
          return;
        }

        case "unknown": {
          toast.danger({
            title: titleWithAction(
              `Request failed (${cls.code})`,
              options?.actionLabel,
            ),
            description: cls.message,
            requestId: cls.requestId,
          });
          return;
        }
      }
    },
    [toast],
  );

  const value = useMemo<DispatchApiError>(() => dispatch, [dispatch]);

  return (
    <ApiErrorDispatcherContext.Provider value={value}>
      {children}
    </ApiErrorDispatcherContext.Provider>
  );
}
